import { GLOBAL_VIEW_DISABLED_PATHS } from './globalViewDisabled'
import { SUPER_FEED_DISABLED_PATHS } from './superFeedViewDisabled'

// ===========================================================================
// Flat menu registry (D-13, PIXMC-6521).
//
// Every destination a persona may be configured to LAND on, keyed by a stable `menuId`.
// This is a developer-facing registry, not a product setting: the only thing that reads
// it is landingPage.js, resolving REACT_APP_LANDING_MENU_<ROLE> after login.
//
// It is NOT the navigation menu. The live menu is still
// components/common/popover/profilePopover.jsx — adding an entry here does not add a
// menu item, and vice versa.
//
// `route` uses the same two substitution tokens as the dormant `menus[]` schema in
// config.js and `createMenu.js`: `account` → 'customers' | 'tenants', `customerId` →
// the selected customer's id. Routes with neither token are absolute.
//
// Scope note: this holds the destinations the profile menu can reach plus each Incident
// Feed tab — the set a landing page can plausibly be pointed at. Deep links
// (/customers/:id/incidents/:eventId and friends) are deliberately absent; they are not
// landable and listing them would be a second route table to keep in sync with App.js.
// ===========================================================================

// The path segment that introduces the Incident Feed's tabs. Lives here rather than in
// the component because the Global-View check below has to know about it.
export const INCIDENT_FEED_SEGMENT = 'incident-feed'

export const menuRegistry = [
  // --- Incident Feed tabs (PIXMC-6442). PixmAdmin-only, same gate as the route. ---
  {
    menuId: 'incidentFeed.overview',
    name: 'Incident Feed — Overview',
    roles: ['PixmAdmin'],
    route: '/account/customerId/incident-feed/overview',
  },
  {
    menuId: 'incidentFeed.threatFeed',
    name: 'Incident Feed — Threat Feed',
    roles: ['PixmAdmin'],
    route: '/account/customerId/incident-feed/threat-feed',
  },
  {
    menuId: 'incidentFeed.policyViolations',
    name: 'Incident Feed — Policy Violations',
    roles: ['PixmAdmin'],
    route: '/account/customerId/incident-feed/policy-violations',
  },
  {
    menuId: 'incidentFeed.saasUsage',
    name: 'Incident Feed — SaaS Usage',
    roles: ['PixmAdmin'],
    route: '/account/customerId/incident-feed/saas-usage',
  },
  {
    menuId: 'incidentFeed.credentialRisks',
    name: 'Incident Feed — Credential Risks',
    roles: ['PixmAdmin'],
    route: '/account/customerId/incident-feed/credential-risks',
  },
  {
    menuId: 'incidentFeed.userActivity',
    name: 'Incident Feed — User Activity',
    roles: ['PixmAdmin'],
    route: '/account/customerId/incident-feed/user-activity',
  },

  // --- Existing destinations, so a persona can be pointed back at today's behaviour ---
  {
    menuId: 'threatFeed',
    name: 'Threat Feed',
    roles: ['PixmAdmin', 'TenantAdmin', 'CustomerAdmin'],
    route: '/account/customerId/incidents',
  },
  {
    menuId: 'licenseManagement',
    name: 'License Management',
    roles: ['PixmAdmin', 'TenantAdmin', 'CustomerAdmin'],
    route: '/account/customerId/license-management',
  },
  {
    menuId: 'deployment',
    name: 'Deployment',
    roles: ['PixmAdmin', 'TenantAdmin', 'CustomerAdmin'],
    route: '/account/customerId/deployment',
  },
  {
    menuId: 'settings',
    name: 'Settings',
    roles: ['PixmAdmin', 'TenantAdmin', 'CustomerAdmin'],
    route: '/customers/customerId/settings',
  },
  {
    menuId: 'policyViolations',
    name: 'Policy Violations',
    roles: ['PixmAdmin'],
    route: '/customers/customerId/policy-violations',
  },
  {
    menuId: 'governance',
    name: 'Governance',
    roles: ['PixmAdmin'],
    route: '/customers/customerId/governance',
  },
  {
    menuId: 'mspCenter',
    name: 'MSP Center',
    roles: ['TenantAdmin'],
    route: '/tenant-admin',
  },
  {
    menuId: 'socIntegration',
    name: 'SOC Integration',
    roles: ['PixmAdmin', 'TenantAdmin', 'CustomerAdmin'],
    route: '/soc-integration',
  },
  {
    menuId: 'threatIntelligence',
    name: 'Threat Intelligence',
    roles: ['PixmAdmin', 'TenantAdmin', 'CustomerAdmin'],
    route: '/threat-intel',
  },
  {
    menuId: 'advancedSettings',
    name: 'Advanced Settings',
    roles: ['PixmAdmin'],
    route: '/advanced-settings',
  },
]

export const findMenuEntry = (menuId) =>
  menuRegistry.find((entry) => entry.menuId === menuId) || null

// `createMenu.js`'s substitution, kept identical so both read the same templates.
//
// Self-guards rather than trusting the caller: a customer-scoped template with no id
// would otherwise produce '/customers/undefined/...', which hits App.js's catch-all and
// re-enters the landing decision — a redirect loop rather than a visible error.
// resolveLandingPath checks this too; this is the second lock on an exported helper.
export const buildMenuPath = (entry, customerId, isGlobalView) => {
  if (!entry) return null
  if (entry.route.includes('customerId') && !customerId) return null
  const account = isGlobalView ? 'tenants' : 'customers'
  return entry.route.replace('account', account).replace('customerId', customerId)
}

// A landing path must survive navbar.handleSelect's redirect rules, which key on the
// LEAF segment of the pathname. For a tabbed route the leaf is the tab ('overview'), not
// the route, so a leaf-only test would be meaningless here — every segment is checked.
//
// 🔴 ...except the tab name itself. Tab segments are identifiers, not routes, and one of
// them COLLIDES with a real disabled path: the Incident Feed has a `policy-violations`
// tab, and `policy-violations` is also the standalone screen that is blocked in Global
// View. Checking it would block the Incident Feed's own tab for the wrong reason. So the
// scan stops at the `incident-feed` segment; anything after it is a tab.
// navbar.handleSelect has the matching guard for the same collision.
const hasDisabledSegment = (path, disabledPaths) => {
  const segments = path.split('/').filter(Boolean)
  const feedIndex = segments.indexOf(INCIDENT_FEED_SEGMENT)
  const checkable = feedIndex === -1 ? segments : segments.slice(0, feedIndex + 1)
  return checkable.some((segment) => disabledPaths.includes(segment))
}

export const isPathAllowedInGlobalView = (path) =>
  !hasDisabledSegment(path, GLOBAL_VIEW_DISABLED_PATHS)

export const isPathAllowedInSuperFeed = (path) =>
  !hasDisabledSegment(path, SUPER_FEED_DISABLED_PATHS)
